import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Stack,
  Typography,
  IconButton,
  Collapse,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate, useLocation } from "react-router-dom";
import { getRequest, postRequest } from "../../api/apiServices";
import apiList from "../../api/apiList";
import "../../styles/Listing/listing-premium.css";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Loader from "../../utils/Loading";
import Toast from "../../utils/Toast";
import CheckBoxIcon from "../../assets/images/checkBoxChecked.svg";
import Popup from "../Common/Popup";

interface CurrentSubscription {
  id: number;
  name: string;
  description: string;
  features: string[];
  slug: string;
  planType: string;
  subscriptionPlanId: string;
  basePrice: number;
  isActive: boolean;
  created_at: Date;
  updated_at: Date;
}

interface Subscription {
  type: string;
  status: "ACTIVE" | "CANCEL";
  startDate: Date;
  endDate: Date;
  isPremium: boolean;
}

interface SubscriptionResponse {
  subscription: Subscription;
  currentSubscription: CurrentSubscription;
}

interface Invoice {
  id: string;
  amount_paid: number;
  billing_reason: string;
  subscription_details: {
    metadata: {
      planType: string;
    };
  };
  hosted_invoice_url?: string;
  invoice_pdf?: string;
}

interface InvoiceResponse {
  invoices: Invoice[];
  has_more: boolean;
}

const Billings = () => {
  const [data, setData] = useState<SubscriptionResponse | null>(null);
  const [invoicesData, setInvoicesData] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [hasMoreInvoices, setHasMoreInvoices] = useState(false);
  const [lastInvoiceId, setLastInvoiceId] = useState<string | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [popupAction, setPopupAction] = useState<
    "cancel" | "upgrade" | "restore" | null
  >(null);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  //Fetch Billings Informations
  let fetchBillings = async () => {
    try {
      setIsLoading(true);
      const response: any = await getRequest(
        `${apiList.getBillings}?ts=${Date.now()}`
      );
      if (response?.data?.success === true) {
        const data = response.data.data;
        setData(data);
      }
    } catch (error) {
      console.error("Error - Fetching billimgs:- " + error);
    } finally {
      setIsLoading(false);
    }
  };

  //Fetch Invoices Informations
  let fetchInvoices = async (
    startingAfter?: string,
    append: boolean = false
  ) => {
    try {
      setIsLoadingInvoices(true);
      const requestBody: any = {
        limit: 10,
      };

      if (startingAfter) {
        requestBody.startingAfter = startingAfter;
      }

      const response: any = await postRequest(apiList.getInvoices, requestBody);
      if (response?.data?.success === true) {
        const invoiceResponse: InvoiceResponse = response.data.data;
        const invoices = invoiceResponse.invoices;
        const hasMore = invoiceResponse.has_more;

        if (append) {
          // Append new invoices to existing ones
          setInvoicesData((prevInvoices) => [...prevInvoices, ...invoices]);
        } else {
          // Replace invoices (initial load)
          setInvoicesData(invoices);
        }

        setHasMoreInvoices(hasMore);

        // Set the last invoice ID for pagination
        if (invoices.length > 0) {
          setLastInvoiceId(invoices[invoices.length - 1].id);
        }
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  // Handle Load More Invoices
  const handleLoadMoreInvoices = () => {
    if (lastInvoiceId && hasMoreInvoices) {
      fetchInvoices(lastInvoiceId, true);
    }
  };

  //Handle Update
  const handleUpdateClick = () => {
    if (data?.subscription?.type == "FREE") {
      navigate("/subscriptions");
    } else {
      navigate("/update-subscriptions");
    }
  };

  //Handle Cancel
  const handleSubscriptionCancel = async () => {
    try {
      setIsLoading(true);
      const response: any = await getRequest(apiList.cancelSubscriptions);
      if (response?.data?.success) {
        Toast(
          response?.data?.statusMessage ||
            "Subscription cancelled successfully!"
        );
      } else {
      }
    } catch (error) {
      console.error("Error from cancel the subscription.  " + error);
    } finally {
      setIsLoading(false);
      fetchBillings();
    }
  };

  //Handle Restore
  const handleRestoreClick = async () => {
    try {
      setIsLoading(true);
      const response: any = await getRequest(apiList.restoreSubscriptions);
      if (response?.data?.success) {
        Toast(
          response?.data?.statusMessage || "Subscription restored successfully!"
        );
      }
    } catch (error) {
      console.error("Error - Restore the Subscriptions:- " + error);
    } finally {
      setIsLoading(false);
      fetchBillings();
    }
  };

  useEffect(() => {
    fetchBillings();
    fetchInvoices(); // Initial load without startingAfter
  }, []);

  // If navigated here right after an update, force a quick refetch and a short retry
  useEffect(() => {
    const state: any = (location && (location as any).state) || {};
    if (state?.updatedAt) {
      fetchBillings();
      const retry = setTimeout(() => {
        fetchBillings();
      }, 800);
      return () => clearTimeout(retry);
    }
  }, [location]);

  const formatAmount = (amount: number) => {
    let forAmt = amount / 100;
    return `$${forAmt}`;
  };

  if (isLoading) {
    return (
      <div className="centered-loader">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <Box className="listing-page">
        <Box className="listing-hero">
          <Box className="listing-hero-inner">
            <Box>
              <Box className="listing-hero-badge">
                <ReceiptLongOutlinedIcon sx={{ fontSize: 14 }} />
                Billings
              </Box>
              <h1 className="listing-hero-title">Billings & Invoices</h1>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5 }}>
                <span className="listing-hero-chip">
                  {data?.subscription?.status || "Unknown"}
                </span>
                <span className="listing-hero-chip">
                  Plan: {data?.subscription?.type || "N/A"}
                </span>
              </Box>
            </Box>

            <button type="button" className="listing-hero-btn" onClick={handleUpdateClick}>
              Upgrade Plan
            </button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Box className="listing-panel">
              {data ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: { xs: "22px", sm: "26px" },
                          fontWeight: 700,
                          color: "#273848",
                        }}
                      >
                        {data?.currentSubscription?.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#84E1CC",
                          marginTop: 0.5,
                        }}
                      >
                        ${data?.currentSubscription?.basePrice}/month
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        padding: "8px 16px",
                        background:
                          data?.subscription?.status === "ACTIVE"
                            ? "linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)"
                            : "linear-gradient(135deg, #fecaca 0%, #fca5a5 100%)",
                        color:
                          data?.subscription?.status === "ACTIVE"
                            ? "#065f46"
                            : "#991b1b",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: 700,
                        textTransform: "uppercase",
                        border:
                          data?.subscription?.status === "ACTIVE"
                            ? "1.5px solid #6ee7b7"
                            : "1.5px solid #f87171",
                      }}
                    >
                      {data?.subscription?.status || "INACTIVE"}
                    </Box>
                  </Box>

                  {/* Features List - Scrollable */}
                  <Box
                    sx={{
                      maxHeight: showAllFeatures ? "none" : "280px",
                      overflowY: showAllFeatures ? "visible" : "auto",
                      paddingRight: 1,
                      marginBottom: 2,
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: "#f1f1f1",
                        borderRadius: "10px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: "#84E1CC",
                        borderRadius: "10px",
                        "&:hover": {
                          background: "#56c6a9",
                        },
                      },
                    }}
                  >
                    <Stack spacing={1.5}>
                      {data?.currentSubscription?.features?.map(
                        (feature, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                            }}
                          >
                            <CheckCircleIcon
                              sx={{
                                fontSize: 20,
                                color: "#84E1CC",
                                marginTop: 0.2,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              sx={{
                                color: "#475569",
                                fontSize: "14px",
                                lineHeight: 1.6,
                              }}
                            >
                              {feature}
                            </Typography>
                          </Box>
                        )
                      )}
                    </Stack>
                  </Box>

                  {/* Show More/Less Toggle */}
                  {data?.currentSubscription?.features &&
                    data.currentSubscription.features.length > 8 && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          marginBottom: 2,
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => setShowAllFeatures(!showAllFeatures)}
                          sx={{
                            textTransform: "none",
                            color: "#84E1CC",
                            fontWeight: 600,
                            "&:hover": {
                              background: "rgba(132, 225, 204, 0.1)",
                            },
                          }}
                          endIcon={
                            showAllFeatures ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )
                          }
                        >
                          {showAllFeatures ? "Show Less" : "Show More"}
                        </Button>
                      </Box>
                    )}
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        borderRadius: "12px",
                        padding: "12px",
                        textTransform: "none",
                        background:
                          "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
                        fontWeight: 600,
                        fontSize: "15px",
                        boxShadow: "0 4px 12px rgba(132, 225, 204, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 16px rgba(132, 225, 204, 0.4)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                      onClick={() => {
                        setPopupAction(
                          data?.subscription?.status === "ACTIVE"
                            ? "upgrade"
                            : "restore"
                        );
                        setIsPopupOpen(true);
                      }}
                    >
                      {data?.subscription?.status === "ACTIVE"
                        ? "Upgrade Plan"
                        : "Restore Plan"}
                    </Button>
                    {data?.subscription?.type !== "FREE" && (
                      <Button
                        variant="outlined"
                        fullWidth
                        sx={{
                          borderRadius: "12px",
                          padding: "12px",
                          textTransform: "none",
                          borderColor: "#ef4444",
                          color: "#ef4444",
                          fontWeight: 600,
                          fontSize: "15px",
                          "&:hover": {
                            borderColor: "#dc2626",
                            background: "rgba(239, 68, 68, 0.05)",
                          },
                        }}
                        onClick={() => {
                          setPopupAction("cancel");
                          setIsPopupOpen(true);
                        }}
                      >
                        Cancel Subscription
                      </Button>
                    )}
                  </Stack>
                </>
              ) : (
                <Typography>Please purchase subscription first.</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box className="listing-panel">
              <h2 className="listing-panel-title">Invoices</h2>

              {invoicesData && invoicesData.length > 0 ? (
                <Stack spacing={2}>
                  {invoicesData.map((invoice) => (
                    <Box key={invoice.id} className="listing-card" sx={{ padding: "20px !important" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          alignItems: { xs: "flex-start", sm: "center" },
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        {/* Left Section - Invoice Info */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              marginBottom: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: "12px",
                                background: "linear-gradient(135deg, #56c6a9, #84e1cc)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <ReceiptIcon sx={{ fontSize: 20, color: "#244565" }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#64748b",
                                  marginBottom: 0.3,
                                }}
                              >
                                Invoice ID
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#273848",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={invoice.id}
                              >
                                {invoice.id}
                              </Typography>
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              marginTop: 1.5,
                              flexWrap: "wrap",
                            }}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#64748b",
                                  marginBottom: 0.3,
                                }}
                              >
                                Amount
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "18px",
                                  fontWeight: 700,
                                  color: "#84E1CC",
                                }}
                              >
                                {formatAmount(invoice.amount_paid)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#64748b",
                                  marginBottom: 0.3,
                                }}
                              >
                                Plan Type
                              </Typography>
                              <Box
                                sx={{
                                  padding: "4px 12px",
                                  background: "rgba(86, 198, 169, 0.12)",
                                  color: "#345e83",
                                  borderRadius: "8px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  display: "inline-block",
                                  border: "1px solid rgba(132, 225, 204, 0.3)",
                                }}
                              >
                                {invoice?.subscription_details?.metadata
                                  ?.planType || "N/A"}
                              </Box>
                            </Box>
                          </Box>
                        </Box>

                        {/* Right Section - Download Button */}
                        <Box>
                          {invoice?.hosted_invoice_url ? (
                            <Button
                              variant="contained"
                              component={Link}
                              href={invoice.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              startIcon={<DownloadIcon />}
                              sx={{
                                borderRadius: "12px",
                                padding: "10px 20px",
                                textTransform: "none",
                                background:
                                  "linear-gradient(135deg, #56c6a9 0%, #84E1CC 100%)",
                                fontWeight: 600,
                                fontSize: "14px",
                                boxShadow:
                                  "0 4px 12px rgba(132, 225, 204, 0.3)",
                                "&:hover": {
                                  boxShadow:
                                    "0 6px 16px rgba(132, 225, 204, 0.4)",
                                  transform: "translateY(-2px)",
                                },
                                transition: "all 0.3s ease",
                              }}
                            >
                              Download
                            </Button>
                          ) : (
                            <Typography
                              sx={{
                                color: "#94A3B8",
                                fontSize: "14px",
                                fontWeight: 500,
                              }}
                            >
                              N/A
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 6,
                    textAlign: "center",
                  }}
                >
                  <ReceiptIcon
                    sx={{ fontSize: 60, color: "#cbd5e1", marginBottom: 2 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#64748b",
                    }}
                  >
                    No invoices found
                  </Typography>
                </Box>
              )}

              {hasMoreInvoices && (
                <Box sx={{ marginTop: 3, textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMoreInvoices}
                    disabled={isLoadingInvoices}
                    sx={{
                      borderRadius: "12px",
                      padding: "10px 24px",
                      textTransform: "none",
                      borderColor: "#84E1CC",
                      color: "#84E1CC",
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#56c6a9",
                        background: "rgba(132, 225, 204, 0.1)",
                      },
                    }}
                  >
                    {isLoadingInvoices ? "Loading..." : "Load More Invoices"}
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Popup
        isOpen={isPopupOpen}
        onClose={() => {
          setIsPopupOpen(false);
          setPopupAction(null);
        }}
        popupName={
          popupAction === "cancel"
            ? "Cancel Subscription"
            : popupAction === "restore"
            ? "Restore Subscription"
            : "Upgrade Plan"
        }
        popupContain={
          popupAction === "cancel"
            ? "Are you sure you want to cancel your subscription?"
            : popupAction === "restore"
            ? "Are you sure you want to restore your subscription?"
            : "Are you sure you want to upgrade your plan?"
        }
        onYes={() => {
          if (popupAction === "cancel") {
            handleSubscriptionCancel();
          } else if (popupAction === "upgrade") {
            handleUpdateClick();
          } else if (popupAction === "restore") {
            handleRestoreClick();
          }
        }}
        onNo={() => {
          setIsPopupOpen(false);
          setPopupAction(null);
        }}
        yesButtonText={
          popupAction === "cancel"
            ? "Yes, Cancel"
            : popupAction === "restore"
            ? "Yes, Restore"
            : "Yes, Upgrade"
        }
        noButtonText={
          popupAction === "cancel"
            ? "Keep Subscription"
            : popupAction === "restore"
            ? "No"
            : "No"
        }
        yesButtonClass="primary-btn"
      />
    </>
  );
};
export default Billings;
